import React, { useContext } from 'react';
import FilterMatrix from '../../modules/filter_matrix';
import { FilterType } from '../../interfaces/filters';
import { getReviews, getReviewsOverview } from '../../api/review';
import VisibleRatingStars from '../../components/common/visible_rating_stars';
import moment from 'moment';
import { get } from 'lodash';
import { ReviewPublicationStatusType, IReviewsOverview, IReviewFilters } from '../../interfaces/review';
import { IPortalReplyItem, IReply } from '../../interfaces/reply';

import {
    createReply,
    updateReply
} from '../../api/review';

import AppContext from '../../context/app_context';
import { NavLink } from 'react-router-dom';
import type { TableColumn } from 'react-data-table-component';
import ReviewScoreDisplayFormatted from '../../components/common/review_score_display_formatted';
import { TReviewInfo } from '../review_showcase/types';
import { shortFormatDate, shortFormatDateTime } from '../../utils/date';

const replystringTextFormatter = (filter: any) => {

    const filterValue = filter.Value === 'true' 
        ? 'Has a Reply (Published or Pending Verification)'
        : 'Has No Reply';

    return `${filter.FilterFormLabel}: ${filterValue}`;
};


export const createFilters = (publicationStatusType?: ReviewPublicationStatusType) => {
    const isPublished: boolean = publicationStatusType === ReviewPublicationStatusType.Published;
    return new FilterMatrix([
        ...(!isPublished ? [
            { Key: 'NonPublicationReason', FilterFormLabel: 'Reason Not Published' , Type: FilterType.LabelValue, FilterPropName: 'PublicationAuxiliaryStatusIDs'}
        ]: []),
        { Key: 'DateSubmittedFrom', FilterFormLabel: 'Date Submitted From' , Type: FilterType.DateFromUTC, FilterPropName: 'DateFrom'},
        { Key: 'DateSubmittedTo', FilterFormLabel: 'Date Submitted To' , Type: FilterType.DateToUTC, FilterPropName: 'DateTo'},
        ...(isPublished? [
            { Key: 'DatePublishedFrom', FilterFormLabel: 'Date Published From' , Type: FilterType.DateFromUTC, FilterPropName: 'LastPublishedDateFrom'},
            { Key: 'DatePublishedTo', FilterFormLabel: 'Date Published To' , Type: FilterType.DateToUTC, FilterPropName: 'LastPublishedDateTo'},
        ]: []),
        { Key: 'ReviewerConnections', FilterFormLabel: 'Reviewer Connection' , Type: FilterType.LabelValue, FilterPropName: 'ReviewerConnectionIDs'},
        { Key: 'ReviewExperience', FilterFormLabel: 'Review Experience' , Type: FilterType.LabelValue, FilterPropName: 'ExperienceTypeIDs'},
        { Key: 'CareTypes', FilterFormLabel: 'Reviewer Care Type' , Type: FilterType.LabelValue, FilterPropName: 'CareTypeID'},
        { Key: 'ReviewText', FilterFormLabel: 'Review Text' , Type: FilterType.String, FilterPropName: 'ReviewText'},
        { Key: 'ReviewerDisplayName', FilterFormLabel: 'Reviewer Display Name', Type: FilterType.String, FilterPropName: 'ReviewerDisplayName'},
        ...(isPublished? [
            { Key: 'HasReply', FilterFormLabel: 'Reply Status', Type: FilterType.String, FilterPropName: 'HasReply', Options: { stringTextFormatter: replystringTextFormatter }}
        ]: []),
        { Key: 'OverallRating', FilterFormLabel: 'Overall Experience Rating' , Type: FilterType.Range, FilterPropName: 'OverallRatingExpr'},
        { Key: 'CleanlinessRating', FilterFormLabel: 'Cleanliness Rating' , Type: FilterType.Range, FilterPropName: 'CleanlinessRatingExpr'},
        { Key: 'FriendlinessRating', FilterFormLabel: 'Friendliness Rating' , Type: FilterType.Range, FilterPropName: 'FriendlinessRatingExpr'},
        { Key: 'SafetySecurity', FilterFormLabel: 'Safety / Security Rating' , Type: FilterType.Range, FilterPropName: 'SafetySecurityRatingExpr'},
        { Key: 'NursingCareRating', FilterFormLabel: 'Nursing Care Rating' , Type: FilterType.Range, FilterPropName: 'NursingCareRatingExpr'},
        { Key: 'StaffRating', FilterFormLabel: 'Staff Rating' , Type: FilterType.Range, FilterPropName: 'StaffRatingExpr'},
        { Key: 'DiningRating', FilterFormLabel: 'Meals & Dining Rating' , Type: FilterType.Range, FilterPropName: 'DiningRatingExpr'},
        { Key: 'ActivitiesRating', FilterFormLabel: 'Activities Rating' , Type: FilterType.Range, FilterPropName: 'ActivitiesRatingExpr'},
        { Key: 'RehabTherapyRating', FilterFormLabel: 'Rehab / Therapy Rating' , Type: FilterType.Range, FilterPropName: 'RehabTherapyRatingExpr'},
        { Key: 'FacilitiesRating', FilterFormLabel: 'Facilities Rating' , Type: FilterType.Range, FilterPropName: 'FacilitiesRatingExpr'},

        { Key: 'NHID', FilterFormLabel: 'NHID' , Type: FilterType.Number, FilterPropName: 'NHID', Hidden: true},
        { Key: 'ReviewID', FilterFormLabel: 'Review ID' , Type: FilterType.Number, FilterPropName: 'ReviewID'}
    ]);
};

export const getReviewDataParams = (
    requestParams: any,
    pageOffset: number, 
    pageLimit: number,
    sortField: string,
    sortOrder: string,
    publicationStatusType: ReviewPublicationStatusType,
    forExport: boolean
) => {

    return forExport  ?  {
        ...requestParams,
        PublicationStatusKeyName: publicationStatusType,
    } : {
        ...requestParams,         
        PublicationStatusKeyName: publicationStatusType,
        Offset: pageOffset,
        Limit: pageLimit,
        SortOrder: sortOrder || 'DESC',
        SortField: sortField || 'LastPublishedDate'
    };
};

export const createOrUpdateReply = async (reviewID: number, replyData: IReply)
    : Promise<IPortalReplyItem> => {

    const saveFn = replyData.ReplyID ? updateReply : createReply;

    const savedReply = await saveFn(reviewID, replyData);

    const {
        ReplyID,
        ReplierEmail,
        ReplierFirstName,
        ReplierLastName,
        ReplierPosition,
        ReplyContent,
        ReplyStatusReply
    } = savedReply;

    const AuxiliaryPublicationStatusID = get(ReplyStatusReply, 'ReplyAuxiliaryStatusID', null);
    const AuxiliaryStatusKeyName = get(ReplyStatusReply, 'ReplyAuxiliaryStatus.KeyName');
    const AuxiliaryPublicationStatusName  = get(ReplyStatusReply, 'ReplyAuxiliaryStatus.ExternalDescription');

    const reviewReply: IPortalReplyItem = {
        ReplyID,
        ReplierEmail,
        ReplierFirstName,
        ReplierLastName,
        ReplierPosition,
        ReplyContent,
        AuxiliaryPublicationStatusID,
        AuxiliaryStatusKeyName,
        AuxiliaryPublicationStatusName,
    };

    return reviewReply;
};

export const fetchReviewOverviewData = async (params: IReviewFilters) => {

    const resData = await getReviewsOverview(params);

    return resData.data;
};

export const fetchReviewData = async (params: any): Promise<[any[], number, number]> => {

    const data = await getReviews(params);

    const rowData = get(data, 'data', []);
    const totalFilteredRows = get(data, 'totalFilteredRows', 0);
    const totalRows = get(data, 'totalRows', 0);
    const tableData = rowData.map((item: any) => ({
        id: item.ReviewID,
        ...item,
        defaultExpanded: true
    }));

    return [tableData, totalRows, totalFilteredRows];
};

type ReviewColumn =  TableColumn<TReviewInfo>;

export const buildColumns = (mode: 'published' | 'pending' | 'notpublished', actionColumn?: ReviewColumn) => {
    const reviewIdColumn: ReviewColumn = {
        name: 'Review ID',
        selector: (row) => row.ReviewID,
        sortable: true,
        sortField: 'ReviewID',
        width: '*'
    };

    const facilityNameColumn: ReviewColumn = {
        name: 'Facility Name',
        selector: (row) => row.Name,
        sortable: true,
        sortField: 'PropertyName',
        wrap: true,
        id: 'community',
        width: '*'
    };

    const displayNameColumn: ReviewColumn = {
        name: 'Display Name',
        selector: (row) => row.ReviewerDisplayName,
        sortable: true,
        sortField: 'ReviewerDisplayName',
        wrap: true,
        id: 'name',
        width: '*'
    };

    const submittedDateColumn: ReviewColumn = {
        name: 'Submitted Date',
        cell: (row) => shortFormatDate(row.CreatedAt),
        sortable: true,
        sortField: 'CreatedAt',
        id: 'submitted',
        width: '*'
    };

    const publicationDateColumn: ReviewColumn = {    
        name: 'Publication Date',
        cell: (row) => shortFormatDateTime(row.LastPublishedDate),
        sortable: true,
        sortField: 'LastPublishedDate',
        id: 'published',
        width: '*'  
    };

    const pendingPublicationDateColumn: ReviewColumn = {
        name: 'Pending Publication Date',
        cell: (row) => shortFormatDate(row.PendingPublicationDate),
        sortable: true,
        sortField: 'PendingPublicationDate',
        id: 'pending',
        width: '*'
    };

    const notPublishedReasonColumn: ReviewColumn = {
        name: 'Not Published Reason',
        cell: (row) => row.PublicationAuxiliaryExternalDescription,
        sortable: true,
        sortField: 'PublicationAuxiliaryExternalDescription',
        id: 'notpublished',
        width: '200px'
    };

    const overallExperienceColumn: ReviewColumn = {
        name: 'Overall Experience',
        cell: (row) => 
        <VisibleRatingStars 
            keyProp={row.ReviewID} 
            value={row.OverallRating}
        />,
        sortable: true,
        sortField: 'OverallRatingValue',
        id: 'ratings',
        width: '170px'
    };

    const publishedReviewsColumns: ReviewColumn[] = [
        reviewIdColumn,
        facilityNameColumn,
        displayNameColumn,
        submittedDateColumn,
        publicationDateColumn,
        overallExperienceColumn,
        ...(actionColumn ? [actionColumn] : [])
    ];

    const pendingReviewsColumns: ReviewColumn[] = [
        reviewIdColumn,
        facilityNameColumn,
        displayNameColumn,
        submittedDateColumn,
        pendingPublicationDateColumn,
        overallExperienceColumn,
        ...(actionColumn ? [actionColumn] : [])
    ];

    const notPublishedReviewsColumns: ReviewColumn[] = [
        reviewIdColumn,
        facilityNameColumn,
        displayNameColumn,
        submittedDateColumn,
        notPublishedReasonColumn,
        overallExperienceColumn,
        ...(actionColumn ? [actionColumn] : [])
    ];

    return {
        published: publishedReviewsColumns,
        pending: pendingReviewsColumns,
        notpublished: notPublishedReviewsColumns
    }[mode];
};

export const buildOverviewColumns = () => {
    const appContext: any = useContext(AppContext);
    const cols: TableColumn<IReviewsOverview>[] = [
        {
            name: 'Facility Name',
            selector: (row) => row.Name,
            cell: (row) => <div className="py-2">
                <div><strong>{row.Name}</strong></div>
                <div>{row.AddressFormatted}</div>
            </div>,
            sortable: true,
            width: appContext.isMobile ? '180px' : '270px',
            wrap: true,
        },
        {
            name: 'Total Number of Published Reviews',
            selector: (row) => row.TotalReviewCount || 0,
            cell: (row) => <div>
                {
                    row.TotalReviewCount ?
                        <NavLink 
                            className="link" 
                            to='/reviews'
                            state={{ 
                                filters: { 
                                    NHID: row.NHID,
                                }
                            }}
                        >
                            {row.TotalReviewCount}
                        </NavLink>
                    : 
                        '0'
                }
            </div>,
            sortable: true,
            width: appContext.isMobile ? '180px' : '200px',
        },
        {
            name: 'Number of Published Reviews in last 12 months',
            selector: (row) => row.TotalReviewWithin12MonthsCount || 0,
            cell: (row) => <div>
                {
                    row.TotalReviewWithin12MonthsCount ?
                        <NavLink 
                            className="link" 
                            to='/reviews'
                            state={{ 
                                filters: { 
                                    NHID: row.NHID,
                                    DatePublishedFrom: moment().subtract(12, 'months').toLocaleString()
                                }
                            }}
                        >
                            {row.TotalReviewWithin12MonthsCount}
                        </NavLink>
                    :
                        '0'
                }
            </div>,
            sortable: true,
            width: appContext.isMobile ? '180px' : '200px',
        },
        {
            name: 'Date of Last Published Review',
            selector: (row) => row.MostRecentReviewDate || '',
            cell: (row) => <div>
                <span className='label label-dot label-success mr-2'></span>
                <span>
                    { moment(row.MostRecentReviewDate).isValid() ? moment(row.MostRecentReviewDate).format('Do MMM YYYY') : 'N/A' }
                </span>
            </div>,
            sortable: true,
            width: '250px',
        },
        {
            name: 'Published Reviews in Last 90 days with No Reply',
            selector: (row) => row.TotalReviewsWithin90DaysNoReply || 0,
            cell: (row) => <div>
                {
                    row.TotalReviewsWithin90DaysNoReply ?
                        <NavLink 
                            className="link"
                            to='/reviews'
                            state={{ 
                                filters: { 
                                    NHID: row.NHID,
                                    HasReply: false,
                                    DatePublishedFrom: moment().subtract(90, 'days').toLocaleString()
                                }
                            }}
                        >
                            {row.TotalReviewsWithin90DaysNoReply}
                        </NavLink>
                    : 
                        '0'
                }
            </div>,
            sortable: true,
            width: appContext.isMobile ? '180px' : '200px',
        },
        {
            name: 'Review Score',
            selector: (row) => row.ReviewScoreDisplay || 0,
            cell: (row) => row.ReviewScoreDisplay ? (
                <div className="flex flex-col justify-items-center items-center py-2">
                    <ReviewScoreDisplayFormatted 
                        value={row.ReviewScoreDisplay}
                        showAsNotApplicableOnNull={true}
                    />
                    <NavLink 
                        className="link"
                        to='/review-score'
                        state={{ selNHID: row.NHID, filters: { NHID: row.NHID } } }
                    >
                        <strong>View Breakdown</strong>
                    </NavLink>
                </div>
            ) : <div className="py-2">{'N/A'}</div>,
            sortable: true,
            width: appContext.isMobile ? '180px' : '200px',
        },
        {
            name: 'Generate Reviews',
            cell: (row) => <div className='py-2' style={{ display: 'flex', flexDirection: 'column' }}>
                <NavLink 
                    className="link"
                    to='/review-cards'
                    state={{ selNHID: row.NHID, NHID: row.NHID } }
                >
                    <strong>Order Cards</strong>
                </NavLink>
                <NavLink 
                    className="link"
                    to='/invite-to-review'
                    state={{ NHID: row.NHID } }
                >
                    <strong>Invite to Review</strong>
                </NavLink>
            </div>,
            sortable: false,
            width: appContext.isMobile ? '180px' : '200px',
        },
    ];
    return cols;
};


export const buildLoadingMessage = (filterMatrix: FilterMatrix) => {

    const numAppliedFilters = filterMatrix.getActiveCount();

    const msg = numAppliedFilters > 0 ? 
        `Applying ${numAppliedFilters} ${numAppliedFilters === 1 ? 'filter' : 'filters'} - see results below`
        : 'Fetching reviews - see results below';

    return msg;
};